import Loading from '@/shared/components/Loading';
import { useBlockingGuardIntegrity } from '@/shared/hooks/useBlockingGuardIntegrity';
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BlockedAppContent } from './BlockedAppContent';

interface BlockingGateProps {
  children: ReactNode;
  isActive: boolean;
  expectsDialog: boolean;
  isLoading?: boolean;
  dialog: ReactNode;
}

export default function BlockingGate({
  children,
  isActive,
  expectsDialog,
  isLoading = false,
  dialog,
}: BlockingGateProps) {
  const shouldShowGuardLayer = isActive || isLoading;

  const [guardKey, setGuardKey] = useState(0);
  const guardHostRef = useRef<HTMLDivElement>(null);
  const blockedContentRef = useRef<HTMLDivElement>(null);

  const handleGuardRestore = useCallback(() => {
    setGuardKey((current) => current + 1);
  }, []);

  useBlockingGuardIntegrity({
    active: isActive,
    expectsDialog,
    guardHostRef,
    blockedContentRef,
    onRestore: handleGuardRestore,
  });

  const guardLayer =
    shouldShowGuardLayer &&
    createPortal(
      <div ref={guardHostRef} data-blocking-guard-root key={guardKey}>
        {isLoading && (
          <div
            data-blocking-guard-overlay
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-bg-overlay opacity-90"
          >
            <Loading size="lg" />
          </div>
        )}

        {dialog}
      </div>,
      document.body
    );

  return (
    <>
      <BlockedAppContent ref={blockedContentRef} shouldBlockInteraction={isActive}>
        {children}
      </BlockedAppContent>

      {guardLayer}
    </>
  );
}
